import type { EntrSession } from '@/domain/types';
import type { EntrDataTransport, TransportResult } from '@/transport/types';
import { parseEntrScanCode } from './scanCodeParser';

export interface SessionLookupObserver {
  addBreadcrumb(
    category: string,
    message: string,
    data?: Record<string, unknown>
  ): void;
  captureException(error: unknown, tag: string): void;
}

export interface UseSessionLookupOptions {
  getSessionByGroup: EntrDataTransport['getSessionByGroup'];
  getSessionByTicketCode: EntrDataTransport['getSessionByTicketCode'];
  canHandleTicketCode: () => boolean;
  setBusy: (value: boolean) => void;
  onSessionResult: (
    result: TransportResult<EntrSession>,
    retry?: () => void
  ) => void;
  showTicketNotFoundError: () => void;
  showNetworkError: (retry: () => void) => void;
  observer: SessionLookupObserver;
}

export function useSessionLookup(options: UseSessionLookupOptions) {
  let lookupSequence = 0;
  let activeLookupId = 0;

  async function selectGroup(groupId: string): Promise<void> {
    const lookupId = beginLookup();
    options.setBusy(true);
    options.observer.addBreadcrumb('entr.group', 'Group selected', {
      group_id: groupId,
    });
    await runLookup(
      lookupId,
      () => options.getSessionByGroup(groupId),
      () => void selectGroup(groupId),
      'select_group'
    );
  }

  async function handleTicketCode(ticketCode: string): Promise<void> {
    if (!options.canHandleTicketCode()) return;
    if (hasActiveLookup()) {
      options.observer.addBreadcrumb(
        'entr.scan',
        'Scan ignored while lookup is active',
        {
          // Ticket codes are operational join keys, not credentials. Keep them
          // intact so overlapping scans can be matched to Sentry and backend
          // lookup records.
          ticketCode,
          ticket_length: ticketCode.length,
        }
      );
      return;
    }

    const parsedScanCode = parseEntrScanCode(ticketCode);
    if (!parsedScanCode) {
      options.observer.addBreadcrumb(
        'entr.scan',
        'Invalid ticket scan rejected before lookup',
        {
          // Keep the rejected scan value intact for support/debugging; ENTR
          // QR/ticket codes are stored operational metadata, not secrets.
          ticketCode,
          ticket_length: ticketCode.length,
        }
      );
      options.showTicketNotFoundError();
      return;
    }

    const lookupTicketCode = parsedScanCode.code;
    const lookupId = beginLookup();
    options.setBusy(true);
    options.observer.addBreadcrumb('entr.scan', 'Ticket scanned', {
      // Ticket code is the primary ENTR operational join key; keep exact value.
      ticketCode: lookupTicketCode,
      ticket_length: lookupTicketCode.length,
      scan_source: parsedScanCode.source,
    });
    await runLookup(
      lookupId,
      () => options.getSessionByTicketCode(lookupTicketCode),
      () => void handleTicketCode(ticketCode),
      'scan_ticket'
    );
  }

  async function runLookup(
    lookupId: number,
    load: () => Promise<TransportResult<EntrSession>>,
    retry: () => void,
    tag: string
  ): Promise<void> {
    try {
      const result = await load();
      if (!isActiveLookup(lookupId)) return;
      options.onSessionResult(result, retry);
    } catch (error) {
      if (!isActiveLookup(lookupId)) return;
      options.observer.captureException(error, tag);
      options.showNetworkError(retry);
    } finally {
      if (isActiveLookup(lookupId)) {
        activeLookupId = 0;
        options.setBusy(false);
      }
    }
  }

  function beginLookup(): number {
    lookupSequence += 1;
    activeLookupId = lookupSequence;
    return activeLookupId;
  }

  function isActiveLookup(lookupId: number): boolean {
    return activeLookupId === lookupId;
  }

  function hasActiveLookup(): boolean {
    return activeLookupId !== 0;
  }

  function invalidateLookup(): void {
    activeLookupId = 0;
  }

  return {
    selectGroup,
    handleTicketCode,
    invalidateLookup,
  };
}
